import type { DBSchema, IDBPDatabase } from 'idb';
import { openStore, openWithin, reportOpenFailure, sweepTemporaryStores, temporaryStoreName } from './storeOpen.js';

/**
 * The app's rows, held locally, so that every filter and every total can be
 * computed on this device whether or not there is a connection (ADR-0025).
 *
 * Three stores now exist and each answers a different question, which is why
 * none of them replaces another:
 *
 *  - `Replica` (replica.ts) — one row per FIELD, fetched per record. The shape
 *    the field-lease editor needs: current value, version, history.
 *  - `DocCache` (docCache.ts) — whole REST responses, keyed by request. What
 *    is left of it after this is the handful of reads that really are
 *    documents: `/me`, `fx`, prefs.
 *  - `Dataset` (here) — every ROW of every replicated entity. The shape an
 *    aggregation needs, because "sum every split in this wallet" over a
 *    field-keyed store is a join the client would have to write.
 *
 * **Cache only, never system of record** — the same stance as the other two,
 * and the reason Safari evicting IndexedDB after a week of neglect is a
 * non-event: an empty `Dataset` re-snapshots. The one genuinely unrecoverable
 * thing on this device is the unsent outbox, which is a separate database on
 * purpose.
 *
 * Reads are synchronous against an in-memory mirror, written through to
 * IndexedDB. A UI cannot await a render — the same constraint `Replica`
 * carries, for the same reason.
 */

/** Any projection row as the `_v` view shapes it: `id`, the fields, `version`, `deleted_at`. */
export interface DatasetRow {
  id: string;
  version?: number;
  deleted_at?: string | null;
  [field: string]: unknown;
}

interface StoredRow {
  /** `${entity}:${id}` — a compound keyPath would work, but this keeps the store schema flat. */
  key: string;
  entity: string;
  row: DatasetRow;
}

/** One transaction's assembled history, as the server builds it. */
interface StoredHistory {
  id: string;
  payload: unknown;
}

interface DatasetSchema extends DBSchema {
  rows: {
    key: string;
    value: StoredRow;
    indexes: { by_entity: string };
  };
  meta: { key: string; value: string };
  history: { key: string; value: StoredHistory };
}

/**
 * A position in the server's arrival log — the id of the newest log row this
 * device has safely consumed. Opaque here: only the server interprets it.
 */
export type Cursor = string;

export interface PageCursor {
  entity: string;
  /**
   * `changed_at` of the last row the page sent, in epoch milliseconds — the
   * delta's tie-break within one millisecond. Opaque here, like `Cursor`:
   * the client's only job is to hand back exactly what it was given.
   *
   * A snapshot page sends `0`; it pages by id alone and the field is along
   * for the ride so one wire format serves both endpoints.
   */
  ms: number;
  id: string;
}

export interface SnapshotBody {
  rows: Record<string, DatasetRow[]>;
  cursor: Cursor;
  /** Present means this is not the whole snapshot; ask again from here. */
  next?: PageCursor;
}

export interface ChangesBody {
  rows: Record<string, DatasetRow[]>;
  cursor: Cursor;
  restart: boolean;
  /** Present means this is not the whole delta; ask again within `until`. */
  next?: { until: Cursor; after: PageCursor };
}

export interface DatasetOptions {
  appId: string;
  env: 'prod' | 'test';
  /**
   * Which record's history a changed row belongs to, or nothing.
   *
   * Supplied by the app rather than assumed here, because "a split's history
   * is part of its transaction's" is a fact about webledger's document groups
   * and not about replication. Returning a id marks that record's stored
   * history stale, so the next pass refetches it.
   */
  historyOwnerOf?: (entity: string, row: DatasetRow) => string | undefined;
}

/**
 * The replica's shape, bumped when a store filled by an older build would be
 * wrong in a way nothing else can detect.
 *
 * Not a schema version and not a protocol version: it answers one question,
 * "would a store filled before this change still be right?". Bump it when the
 * set of replicated entities changes, or when what a row means changes.
 *
 *  1 — deleted rows are kept rather than dropped (ADR-0025, 2026-10-06)
 *  2 — a transaction carries its legs; `split` is no longer replicated
 *      (ADR-0026)
 */
export const REPLICA_SHAPE = 2;

export class Dataset {
  private db: IDBPDatabase<DatasetSchema> | undefined;
  /** entity -> id -> row. Live rows only; a deleted row is removed, not flagged. */
  private readonly mirror = new Map<string, Map<string, DatasetRow>>();
  private readonly listeners = new Set<() => void>();
  private mark: Cursor | undefined;
  private staging = false;
  /** The replica shape this store was filled for. See `REPLICA_SHAPE`. */
  private shape = 0;
  /** See `setPendingDeletes` — entity -> record ids hidden by an unsent delete. */
  private pendingDeletes = new Map<string, Set<string>>();
  /** See `setPendingRestores` — entity -> record ids shown despite a server tombstone. */
  private pendingRestores = new Map<string, Set<string>>();
  /** See `setPendingRows` — entity -> id -> the row as unsent work says it is, or null for gone. */
  private pendingRows = new Map<string, Map<string, DatasetRow | null>>();
  private lastSyncedAt: string | undefined;
  /** Bumped by every change to the mirror. See `revision`. */
  private rev = 0;

  constructor(private readonly opts: DatasetOptions) {}

  /** Set when this page is working from a temporary store — see `open`. */
  private temporaryName: string | undefined;

  /**
   * Open this device's copy, or — when it will not open within
   * `fallbackAfterMs` — a fresh temporary one for this page (2026-10-21).
   *
   * The copy is shared by every tab, and a tab can be kept waiting on it by
   * another one; that drew pages with headers and no data until every window
   * was closed. A temporary store is empty, so the page fills it from the
   * server like a first visit and works; `isTemporary` lets it say so, and
   * `discardTemporary` removes it when the page goes. The real store, if it
   * opens after all, is closed rather than used.
   */
  async open(opts: { fallbackAfterMs?: number } = {}): Promise<void> {
    const name = `colplat-rows-${this.opts.appId}-${this.opts.env}`;
    const upgrade = (db: IDBPDatabase<DatasetSchema>, from: number): void => {
      if (from < 1) {
        const rows = db.createObjectStore('rows', { keyPath: 'key' });
        rows.createIndex('by_entity', 'entity');
        db.createObjectStore('meta');
      }
      if (from < 2) db.createObjectStore('history', { keyPath: 'id' });
    };
    const real = openStore<DatasetSchema>(name, 2, upgrade);
    let opened: IDBPDatabase<DatasetSchema> | undefined;
    try {
      opened = opts.fallbackAfterMs === undefined ? await real : await openWithin(real, opts.fallbackAfterMs, name);
    } catch (err) {
      // It would not open at all — most often this page's code is older than
      // the stored copy (`reportOpenFailure`). A copy is a copy: carry on with
      // a temporary one rather than draw nothing.
      if (opts.fallbackAfterMs === undefined) throw err;
      reportOpenFailure(name, err);
    }
    if (opened) {
      this.db = opened;
      sweepTemporaryStores(name);
      await this.hydrate();
      return;
    }
    void real.then((late) => late.close()).catch(() => undefined);
    this.temporaryName = temporaryStoreName(name);
    this.db = await openStore<DatasetSchema>(this.temporaryName, 2, upgrade);
    await this.hydrate();
  }

  /** This page could not open the device's copy and is working from a temporary one. */
  isTemporary(): boolean {
    return this.temporaryName !== undefined;
  }

  /** Remove the temporary store — when the page goes, so they do not pile up. */
  discardTemporary(): void {
    if (!this.temporaryName) return;
    this.db?.close();
    indexedDB.deleteDatabase(this.temporaryName);
  }

  private requireDb(): IDBPDatabase<DatasetSchema> {
    if (!this.db) throw new Error('Dataset.open() must be awaited before use');
    return this.db;
  }

  private async hydrate(): Promise<void> {
    const db = this.requireDb();
    for (const stored of await db.getAll('rows')) this.put(stored.entity, stored.row);
    for (const stored of await db.getAll('history')) this.histories.set(stored.id, stored.payload);
    // `cursor`, not the old `watermark` key. A device upgrading across the
    // 2026-10-03 change finds no `cursor` and takes a fresh snapshot, which
    // is what should happen: its old value describes a log position in a
    // numbering this server no longer speaks.
    this.mark = await db.get('meta', 'cursor');
    this.staging = (await db.get('meta', 'staging')) === '1';
    /*
     * What shape this store was filled for.
     *
     * Some changes to the replica cannot be repaired by a delta or noticed by
     * the digest, because both only ever speak about rows the server still
     * sends. Deleted rows arriving where they used to be dropped was one;
     * an entity leaving the replicated set is another. Neither leaves any
     * trace a device could spot — it just quietly holds a shape nobody serves
     * any more, and believes it.
     *
     * So the store records which shape it was filled for, and a store filled
     * for an older one re-snapshots once. Written by `finishSnapshot`, which
     * is the only thing that fills a store completely.
     */
    this.shape = Number(await db.get('meta', 'shape') ?? 0);
    this.lastSyncedAt = await db.get('meta', 'syncedAt');
    this.notify();
  }

  // ------------------------------------------------------------------ reads

  /*
   * `T` is bounded by `{ id: string }` rather than by `DatasetRow`.
   *
   * `DatasetRow` carries an index signature so that a row of any entity can be
   * stored without naming its fields; a hand-written row type (the ones in
   * `apps/webledger/domain/rows.ts`) deliberately does not, because an index
   * signature would let a typo compile. A type without one does not extend a
   * type with one, so bounding on `DatasetRow` here would reject exactly the
   * types this is for.
   */
  /** Every live row of one entity. Synchronous — see the class comment. */
  rows<T extends { id: string } = DatasetRow>(entity: string): T[] {
    const all = this.mirror.get(entity);
    const over = this.pendingRows.get(entity);
    if (!over || over.size === 0) {
      if (!all) return [];
      return [...all.values()].filter((r) => this.isLive(entity, r)) as unknown as T[];
    }
    // The server's rows, each replaced by what unsent work says it is now,
    // then the ones only unsent work knows about — a record created here.
    const out: DatasetRow[] = [];
    for (const r of all?.values() ?? []) {
      const mine = over.has(r.id) ? over.get(r.id) : r;
      if (mine && this.isLive(entity, mine)) out.push(mine);
    }
    for (const [id, r] of over) {
      if (r && !all?.has(id) && this.isLive(entity, r)) out.push(r);
    }
    return out as unknown as T[];
  }

  row<T extends { id: string } = DatasetRow>(entity: string, id: string): T | undefined {
    const over = this.pendingRows.get(entity);
    const row = over?.has(id) ? over.get(id) : this.mirror.get(entity)?.get(id);
    return row && this.isLive(entity, row) ? (row as unknown as T) : undefined;
  }

  /**
   * Rows as unsent work on this device says they are (ADR-0011, 2026-10-21).
   *
   * Derived from the outbox, exactly like `setPendingDeletes`, and for the
   * same reason: every list, total and report reads `rows()`, so the one
   * place a queued create or edit is applied is the one place all of them
   * agree. Without it a transaction entered on a train was saved and nowhere
   * to be seen — not in the wallet log, not in the totals — until the
   * connection came back.
   *
   * A row here replaces the server's row with the same id, or stands alone
   * when the server has never seen it; `null` hides one the unsent work has
   * removed (a tag taken off, a debt undone). Nothing is written to the
   * store: forgetting this map is going back to what the server said.
   */
  setPendingRows(byEntity: ReadonlyMap<string, ReadonlyMap<string, DatasetRow | null>>): void {
    this.pendingRows = new Map([...byEntity].map(([e, rows]) => [e, new Map(rows)]));
    this.rev += 1;
    this.notify();
  }

  /**
   * Every live row exactly as the server sent it: no unsent deletes hidden,
   * no unsent restores shown, no unsent creates or edits laid over.
   *
   * What the digest folds, because the digest compares with the SERVER's
   * fold. Folding `rows()` counted local intent as disagreement — a device
   * with one unsent delete refetched that whole entity on every connect —
   * and with unsent creates and edits laid over it, every connect would.
   */
  serverRows<T extends { id: string } = DatasetRow>(entity: string): T[] {
    const all = this.mirror.get(entity);
    if (!all) return [];
    return [...all.values()].filter((r) => !r['deleted_at']) as unknown as T[];
  }

  /** Whether this record is shown as unsent work says it is. */
  isPendingRow(entity: string, id: string): boolean {
    return this.pendingRows.get(entity)?.has(id) ?? false;
  }

  /**
   * Deleted records, newest deletion first — what the "Deleted records" page
   * lists, with no connection needed.
   *
   * A record with an unsent *restore* is not here: to the person who pressed
   * the button it is back, and it has to be back everywhere at once. The
   * mirror image of `pendingDeletes`.
   */
  deletedRows<T extends { id: string } = DatasetRow>(entity: string): T[] {
    const all = this.mirror.get(entity);
    if (!all) return [];
    const coming = this.pendingRestores.get(entity);
    return [...all.values()]
      .filter((r) => r['deleted_at'] && !coming?.has(r.id))
      .sort((a, b) => String(b['deleted_at']).localeCompare(String(a['deleted_at']))) as unknown as T[];
  }

  /**
   * Whether this row counts as present.
   *
   * Three things decide it, and they are applied here rather than at each
   * call site because every list in the app reads through `rows()`: the
   * server's own `deleted_at`, a delete made here and not yet sent (hidden,
   * though the server still has it), and a restore made here and not yet sent
   * (shown, though the server still calls it deleted).
   */
  private isLive(entity: string, row: DatasetRow): boolean {
    if (this.pendingDeletes.get(entity)?.has(row.id)) return false;
    if (this.pendingRestores.get(entity)?.has(row.id)) return true;
    return !row['deleted_at'];
  }

  /**
   * The row as it still exists underneath a deletion that has not been sent.
   *
   * `rows()` and `row()` hide those, because to the person who deleted it the
   * record is gone and every list has to agree. Undo needs to reach it anyway,
   * and so does the replay that may have to put it back when the server
   * refuses the delete.
   */
  deletedLocally<T extends { id: string } = DatasetRow>(entity: string, id: string): T | undefined {
    if (!this.pendingDeletes.get(entity)?.has(id)) return undefined;
    return this.mirror.get(entity)?.get(id) as unknown as T | undefined;
  }

  /** The row whatever its state — for a restore, which acts on a deleted one. */
  anyRow<T extends { id: string } = DatasetRow>(entity: string, id: string): T | undefined {
    return this.mirror.get(entity)?.get(id) as unknown as T | undefined;
  }

  /**
   * Clear the tombstone on records the server has just confirmed restored.
   *
   * Applied from the server's own answer rather than inferred, and applied at
   * once rather than waiting for the delta — which `SAFE_LAG_MS` holds back
   * by a minute, long enough for somebody to press the button again or
   * conclude it did nothing.
   */
  async markRestored(members: ReadonlyArray<{ entity: string; recordId: string }>): Promise<void> {
    const db = this.requireDb();
    const tx = db.transaction('rows', 'readwrite');
    for (const m of members) {
      const row = this.mirror.get(m.entity)?.get(m.recordId);
      if (!row) continue;
      const revived = { ...row, deleted_at: null };
      this.put(m.entity, revived);
      await tx.store.put({ key: `${m.entity}:${m.recordId}`, entity: m.entity, row: revived });
    }
    await tx.done;
    this.notify();
  }

  /** Records restored here and not yet accepted by the server. See `isLive`. */
  setPendingRestores(byEntity: ReadonlyMap<string, ReadonlySet<string>>): void {
    this.pendingRestores = new Map([...byEntity].map(([e, ids]) => [e, new Set(ids)]));
    // Which rows count as present has changed, which is a row change to
    // every reader even though no row was rewritten.
    this.rev += 1;
    this.notify();
  }

  isPendingRestore(entity: string, id: string): boolean {
    return this.pendingRestores.get(entity)?.has(id) ?? false;
  }

  count(entity: string): number {
    return this.rows(entity).length;
  }


  /**
   * Records deleted on this device and not yet accepted by the server.
   *
   * Derived state, set from the outbox and never stored here: the queued
   * delete IS the fact, and a second copy would be a second thing to keep
   * right. What this adds is that every view agrees at once — the wallet log,
   * the register, reports, budgets and the totals bar all read through
   * `rows()`, so hiding it in one place is the difference between a deletion
   * that looks done and one that looks done in some lists and not others.
   *
   * Nothing is removed from the store. The row stays exactly where it was,
   * so putting it back is forgetting this set, not re-fetching anything —
   * which is what makes undo work with no connection.
   */
  setPendingDeletes(byEntity: ReadonlyMap<string, ReadonlySet<string>>): void {
    this.pendingDeletes = new Map([...byEntity].map(([e, ids]) => [e, new Set(ids)]));
    // As in `setPendingRestores`: nothing was rewritten, but what is visible
    // has changed.
    this.rev += 1;
    this.notify();
  }

  /** Whether this record is hidden by an unsent delete. */
  isPendingDelete(entity: string, id: string): boolean {
    return this.pendingDeletes.get(entity)?.has(id) ?? false;
  }

  /** Nothing has ever been synced onto this device. */
  isEmpty(): boolean {
    return this.mark === undefined;
  }

  cursor(): Cursor | undefined {
    return this.mark;
  }

  /**
   * Whether the next sync has to be a full snapshot rather than a delta.
   *
   * True when nothing was ever synced, and true when a paged snapshot was
   * interrupted. The second case is the one worth spelling out: a snapshot
   * clears the store before it refills it, so an interruption leaves rows
   * missing with the *old* cursor still in place — and a delta from that
   * cursor would top up a store that is quietly short and report success.
   * The staging flag outlives the crash; the partial store does not get to
   * pass as a whole one.
   */
  needsSnapshot(): boolean {
    return this.mark === undefined || this.staging || this.shape < REPLICA_SHAPE;
  }

  /** ISO-8601 of the last successful sync, for the UI to say how stale this is. */
  syncedAt(): string | undefined {
    return this.lastSyncedAt;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * How many times the ROWS have moved, for a subscriber too expensive to
   * run on a notification that carried nothing.
   *
   * Most notifications carry nothing. A poke fetches a delta, the delta is
   * usually empty, and `commitCursor` notifies anyway because the cursor did
   * move — which is true and is not news to anybody drawing a page. The
   * cheap screens can redraw regardless; a general ledger over the whole
   * book cannot, and neither can one that would rebuild a control somebody
   * has open (invariant #5).
   *
   * So this counts writes to the mirror, and nothing else: not cursors, not
   * sync timestamps, not a snapshot finishing. Compare it with what you last
   * drew; equal means there is nothing to draw.
   */
  revision(): number {
    return this.rev;
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }

  // ----------------------------------------------------------------- writes

  private put(entity: string, row: DatasetRow): void {
    let bag = this.mirror.get(entity);
    if (!bag) { bag = new Map(); this.mirror.set(entity, bag); }
    bag.set(row.id, row);
    this.rev += 1;
  }

  private drop(entity: string, id: string): void {
    this.mirror.get(entity)?.delete(id);
    this.rev += 1;
  }

  /**
   * Start a fresh snapshot: mark the store as half-built, then empty it.
   *
   * Clearing is what makes a snapshot the whole truth — merging one would
   * leave behind any row deleted while this device was away, and nothing
   * later would ever mention that row again. But a snapshot now arrives over
   * several requests, so the window between "cleared" and "refilled" is a
   * network round trip wide and a device can be closed inside it. The flag
   * is written BEFORE the clear and removed only by `finishSnapshot`, so an
   * interrupted snapshot is recognisable as one for ever after rather than
   * passing as a complete store with a few rows missing.
   */
  async beginSnapshot(): Promise<void> {
    const db = this.requireDb();
    this.mirror.clear();
    this.rev += 1;
    this.staging = true;
    const tx = db.transaction(['rows', 'meta'], 'readwrite');
    await tx.objectStore('meta').put('1', 'staging');
    await tx.objectStore('rows').clear();
    await tx.done;
    this.notify();
  }

  /** One page of a snapshot. Writes no cursor: the pages are not the whole. */
  async addRows(rows: Record<string, readonly DatasetRow[]>): Promise<void> {
    const db = this.requireDb();
    const tx = db.transaction('rows', 'readwrite');
    for (const [entity, found] of Object.entries(rows)) {
      for (const row of found) {
        this.put(entity, row);
        await tx.store.put({ key: `${entity}:${row.id}`, entity, row });
      }
    }
    await tx.done;
    this.notify();
  }

  /**
   * Every page arrived: adopt the cursor and stop calling the store partial.
   *
   * Both in one transaction, and in this order relative to everything else —
   * a cursor adopted while the staging flag still stood would be a store
   * claiming to be current and admitting it is not.
   */
  async finishSnapshot(cursor: Cursor): Promise<void> {
    const db = this.requireDb();
    const tx = db.transaction('meta', 'readwrite');
    await tx.store.delete('staging');
    this.staging = false;
    // Filled for the shape this build serves, which is what `needsSnapshot`
    // asks about.
    await tx.store.put(String(REPLICA_SHAPE), 'shape');
    this.shape = REPLICA_SHAPE;
    await this.writeMeta(tx.store, cursor);
    await tx.done;
    this.notify();
  }

  /**
   * Apply a delta.
   *
   * A row arrives carrying its own `deleted_at`, so a delete and a restore are
   * the same message — "here is what this row is now" — and there is no second
   * channel that could disagree with the first. Applying the same delta twice
   * is applying it once, which is what lets the server re-send the boundary
   * rather than risk dropping one.
   */
  async applyChanges(rows: Record<string, readonly DatasetRow[]>): Promise<void> {
    const db = this.requireDb();
    const tx = db.transaction('rows', 'readwrite');
    for (const [entity, found] of Object.entries(rows)) {
      for (const row of found) {
        // Whatever changed, the history of the record it belongs to has moved
        // on — including when the row itself is a child, and including a
        // delete, which is a history event like any other.
        const owner = this.opts.historyOwnerOf?.(entity, row);
        if (owner !== undefined) this.staleHistories.add(owner);
        /*
         * A deleted row is stored, not dropped (2026-10-06).
         *
         * It used to be deleted from the store, which made the local copy
         * unable to answer "what has been deleted" — so restoring anything
         * needed a connection, including something deleted on this very
         * device a moment earlier. Keeping the tombstone is what lets the
         * whole of deleting and restoring work offline.
         *
         * Nothing else sees them: `rows()`, `row()` and `count()` filter on
         * `deleted_at`, so every list, total and report reads exactly what it
         * read before.
         */
        const key = `${entity}:${row.id}`;
        this.put(entity, row);
        await tx.store.put({ key, entity, row });
      }
    }
    await tx.done;
    this.notify();
  }

  /**
   * Adopt a cursor, once every page of whatever produced it has been applied.
   *
   * Separate from `applyChanges` because a delta can span several responses
   * and only the last of them is the whole story. Advancing per page would
   * mean an interruption left the cursor claiming ground the store never
   * covered — the one failure this design cannot recover from. Advancing at
   * the end means an interruption simply re-reads the window, and re-applying
   * rows costs nothing.
   */
  async commitCursor(cursor: Cursor): Promise<void> {
    const db = this.requireDb();
    const tx = db.transaction('meta', 'readwrite');
    await this.writeMeta(tx.store, cursor);
    await tx.done;
    this.notify();
  }

  private async writeMeta(
    store: { put: (v: string, k: string) => Promise<unknown> }, cursor: Cursor,
  ): Promise<void> {
    this.mark = cursor;
    this.lastSyncedAt = new Date().toISOString();
    await store.put(cursor, 'cursor');
    await store.put(this.lastSyncedAt, 'syncedAt');
  }

  // ---------------------------------------------------------------- history

  /*
   * A record's change history, held per transaction rather than as raw rows.
   *
   * The server already assembles this — grouping field writes and record
   * events into commits, and gathering the splits and tag links whose own
   * history belongs to the transaction — and that assembly is the part worth
   * having. Replicating the raw `field_history` instead would mean doing the
   * same grouping again on the client, which is a second implementation of
   * something subtle for no gain: history is append-only and immutable, so a
   * cached answer is never wrong, only ever incomplete.
   *
   * Which is why staleness is tracked by invalidation rather than by
   * comparing versions. A tag added to a transaction changes its history and
   * does NOT bump `transaction.version` — the link is its own record — so a
   * version check would quietly miss exactly the case the history panel was
   * extended to cover.
   */
  private readonly histories = new Map<string, unknown>();
  /** Transactions whose stored history is known to be behind. */
  private readonly staleHistories = new Set<string>();

  history<T>(transactionId: string): T | undefined {
    if (this.staleHistories.has(transactionId)) return undefined;
    return this.histories.get(transactionId) as T | undefined;
  }

  async putHistory(transactionId: string, payload: unknown): Promise<void> {
    this.histories.set(transactionId, payload);
    this.staleHistories.delete(transactionId);
    await this.requireDb().put('history', { id: transactionId, payload });
  }

  /** Mark these transactions' histories as needing a refetch. */
  markHistoryStale(transactionIds: Iterable<string>): void {
    for (const id of transactionIds) this.staleHistories.add(id);
  }

  /** Transactions with no history held, or whose held copy is behind. */
  missingHistories(transactionIds: readonly string[]): string[] {
    return transactionIds.filter((id) => this.history(id) === undefined);
  }

  /**
   * Replace one entity's rows wholesale — what a failed digest repairs with.
   *
   * Narrower than a snapshot and wider than a delta: the rows that arrive are
   * the complete live set for that entity, so anything held and not named has
   * gone and must go here too. Merging would leave exactly the ghost the
   * digest just caught.
   */
  async replaceEntity(entity: string, rows: readonly DatasetRow[]): Promise<void> {
    const db = this.requireDb();
    const previous = this.mirror.get(entity);
    this.mirror.set(entity, new Map(rows.map((r) => [r.id, r])));
    this.rev += 1;

    const tx = db.transaction('rows', 'readwrite');
    for (const id of previous?.keys() ?? []) await tx.store.delete(`${entity}:${id}`);
    for (const row of rows) await tx.store.put({ key: `${entity}:${row.id}`, entity, row });
    await tx.done;
    this.notify();
  }

  /** Forget everything — used when the schema version moves under us. */
  async clear(): Promise<void> {
    const db = this.requireDb();
    this.mirror.clear();
    this.rev += 1;
    this.mark = undefined;
    this.staging = false;
    this.lastSyncedAt = undefined;
    this.histories.clear();
    this.staleHistories.clear();
    const tx = db.transaction(['rows', 'meta', 'history'], 'readwrite');
    await tx.objectStore('rows').clear();
    await tx.objectStore('meta').clear();
    await tx.objectStore('history').clear();
    await tx.done;
    this.notify();
  }
}
